<script>
// Reusable interest-driven recommendation list. Used in three places with the
// same code: the Event-Home teaser (compact), the dedicated Hub view (full),
// and the Django-CMS website plugin via speaker.bundle (full).
// Relative imports only -- also bundled into speaker.bundle.
import RecCard from './rec-card.vue'

export default {
  name: 'RecommendationIndex',
  components: { RecCard },
  props: {
    // Optional event short name; falls back to the current route/storage event,
    // and finally to the backend default (customer.get_next_event()).
    eventShortName: { type: String, default: '' },
    // Compact teaser (Event-Home) vs full 3-section list (Hub / CMS plugin).
    compact: { type: Boolean, default: false },
  },

  data() {
    return {
      exhibitors: [],
      sessions: [],
      members: [],
      loaded: false,
      // Compact teaser: rotate the sessions/exhibitors split per mount so the
      // three slots alternate between 1 session + 2 exhibitors and 2 + 1.
      mixSessionsFirst: Math.random() < 0.5,
    }
  },

  computed: {
    event() {
      return this.eventShortName
        || (this.$route && this.$route.params && this.$route.params.shortName)
        || (this.$storage && this.$storage.observed
            && this.$storage.observed.currentEventShortName)
        || ''
    },
    limit() { return this.compact ? 2 : 5 },
    hasAny() {
      return this.exhibitors.length || this.sessions.length || this.members.length
    },
    // Teaser split: 2+1 or 1+2 across sessions/exhibitors (full lists elsewhere).
    teaserSessions() {
      return this.compact ? this.sessions.slice(0, this.mixSessionsFirst ? 2 : 1) : this.sessions
    },
    teaserExhibitors() {
      return this.compact ? this.exhibitors.slice(0, this.mixSessionsFirst ? 1 : 2) : this.exhibitors
    },
  },

  created() {
    this.load()
  },

  methods: {
    load(retry = true) {
      let me = this
      const params = { limit: this.limit }
      if (this.event) params.event = this.event
      this.$api.get('recommendations/', { params }).then((response) => {
        const d = response.data || {}
        me.exhibitors = d.exhibitors || []
        me.sessions = d.sessions || []
        me.members = d.members || []
        me.loaded = true
      }).catch((error) => {
        // CMS embed token race: webapp-config readToken() is fire-and-forget,
        // so the very first request can arrive before the token is set. Retry
        // once after giving the bootstrap a moment (and nudging fetchUser).
        const status = error && error.response && error.response.status
        if (retry && (status === 401 || status === 403)) {
          if (me.$storage && me.$storage.fetchUser) me.$storage.fetchUser()
          setTimeout(() => me.load(false), 600)
        } else {
          me.loaded = true
        }
      })
    },

    goToHub() {
      const shortName = this.event
      const target = shortName
        ? { name: 'Event Recommendations', params: { shortName } }
        : { name: 'Recommendations' }
      this.$router.push(target).catch(() => {})
    },

    goToInterests() {
      this.$router.push('/user-profile').catch(() => {})
    },
  },
}
</script>

<template>
  <div>

    <!-- COMPACT: Event-Home teaser -->
    <template v-if="compact">
      <div v-if="hasAny" class="my-3">
        <h3 class="h5 mb-2"><i class="bi bi-stars text-primary me-1"></i> Für dich empfohlen</h3>

        <template v-if="members.length">
          <div class="text-muted text-uppercase fw-semibold small my-1">Kontakte</div>
          <div class="list-group mb-2">
            <rec-card v-for="m in members" :key="'m' + m.uuid" :item="m" />
          </div>
        </template>

        <template v-if="teaserSessions.length">
          <div class="text-muted text-uppercase fw-semibold small my-1">Sessions</div>
          <div class="list-group mb-2">
            <rec-card v-for="s in teaserSessions" :key="'s' + s.url" :item="s" />
          </div>
        </template>

        <template v-if="teaserExhibitors.length">
          <div class="text-muted text-uppercase fw-semibold small my-1">Aussteller</div>
          <div class="list-group mb-2">
            <rec-card v-for="e in teaserExhibitors" :key="'e' + e.url" :item="e" />
          </div>
        </template>

        <button class="btn btn-outline-primary btn-sm w-100" @click.prevent="goToHub">
          Alle anzeigen <i class="fas fa-arrow-right ms-1"></i>
        </button>
      </div>
    </template>

    <!-- FULL: Hub view / CMS plugin -->
    <template v-else>
      <div v-if="!loaded" class="text-center text-muted py-4">
        <i class="fas fa-spinner fa-spin"></i>
      </div>

      <div v-else-if="!hasAny" class="text-center text-muted py-4">
        <p class="mb-2">Wähle Interessen, um passende Vorschläge zu erhalten.</p>
        <button class="btn btn-outline-primary btn-sm" @click.prevent="goToInterests">
          Interessen wählen
        </button>
      </div>

      <template v-else>
        <section v-if="members.length" class="mb-4">
          <h3 class="h5 mb-2">Kontakte</h3>
          <div class="list-group">
            <rec-card v-for="m in members" :key="'m' + m.uuid" :item="m" />
          </div>
        </section>

        <section v-if="sessions.length" class="mb-4">
          <h3 class="h5 mb-2">Sessions</h3>
          <div class="list-group">
            <rec-card v-for="s in sessions" :key="'s' + s.url" :item="s" />
          </div>
        </section>

        <section v-if="exhibitors.length" class="mb-4">
          <h3 class="h5 mb-2">Aussteller</h3>
          <div class="list-group">
            <rec-card v-for="e in exhibitors" :key="'e' + e.url" :item="e" />
          </div>
        </section>
      </template>
    </template>

  </div>
</template>
